import { datadogLogs } from '@datadog/browser-logs';
import React, { useEffect, useState } from 'react';

import { UseState } from '../../../../types';
import { LoggerContext } from '../../contexts/logger.context';
import { useLogger } from '../../hooks/useLogger.hook';
import { UseLogger } from '../../interfaces';

import { LoggerProps } from './logger.types';

export const Logger: React.FC<LoggerProps> = (props: LoggerProps): JSX.Element => {
  const {
    applicationId,
    userId,
    clientToken,
    service,
    env,
    version,
    site = 'datadoghq.eu',
    forwardErrorsToLogs = true,
    sampleRate = 100,
    children
  }: LoggerProps = props;
  const [loggerInitialized, setLoggerInitialized]: UseState<boolean> = useState<boolean>(false);
  const { logger }: UseLogger = useLogger({ clientToken, service, env, version, applicationId, userId });
  const windowOnErrorHandler: OnErrorEventHandler = (
    event: Event | string, source?: string, lineNo?: number, columnNo?: number, error?: Error,
  ): boolean => {
    const errorDetails: object = {
      ...{ event },
      ...{ source },
      ...{ lineNo },
      ...{ columnNo },
      errorObject: error,
    };
  
    logger('GlobalError', errorDetails);
    return !!clientToken; // When the function returns true, this prevents the firing of the default event handler.
  };
  
  const windowOnUnhandledRejectionHandler: (event: PromiseRejectionEvent) => void = (event: PromiseRejectionEvent): void => {  
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    logger('UnhandledRejection', { 'reason': event.reason });
  };
  
  const globalErrorHandler: () => void = (): void => {
    window.onerror = windowOnErrorHandler;
    window.onunhandledrejection = windowOnUnhandledRejectionHandler;
  };

  useEffect(
    (): void => {
      if (!loggerInitialized && clientToken) {
        datadogLogs.init({ clientToken, service, env, version, site, forwardErrorsToLogs, sampleRate });
        setLoggerInitialized(true);
      }
    },
    [],
  );

  useEffect((): void => globalErrorHandler(), []);

  return (
    <LoggerContext.Provider value={{ applicationId, userId, clientToken, service, env, version }}>
      {children}
    </LoggerContext.Provider>
  );
};
